import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Landmark, Zap, Scale, ArrowRight, Globe, X, Search, CreditCard } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { formatUSD } from "@/lib/constants";
import { setSeo, resetSeo } from "@/lib/seo";
import { useTranslation, Trans } from "react-i18next";

export default function BanksPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [banks, setBanks] = useState([]);
  const [compareSel, setCompareSel] = useState([]);
  const [q, setQ] = useState("");
  const [typeSel, setTypeSel] = useState("All");
  const ql = q.trim().toLowerCase();

  useEffect(() => {
    api.get("/banks").then((r) => setBanks(r.data.banks || [])).catch(() => {});
  }, []);
  useEffect(() => {
    setSeo({
      title: t("banksPg.seoTitle"),
      description: t("banksPg.seoDesc"),
      url: window.location.href,
    });
    return () => resetSeo();
  }, [t, i18n.language]);

  const toggleCompare = (slug) => setCompareSel((prev) => prev.includes(slug) ? prev.filter((s) => s !== slug) : (prev.length >= 3 ? prev : [...prev, slug]));
  const nameOf = (slug) => banks.find((b) => b.slug === slug)?.name || slug;
  const goCompare = () => navigate(`/bank-compare?b=${compareSel.join(",")}`);

  const groups = [
    { key: "Digital", label: t("banksPg.groupDigital"), icon: Zap },
    { key: "Traditional", label: t("banksPg.groupTraditional"), icon: Landmark },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative grain overflow-hidden border-b border-border bg-secondary/40">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-14">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-2xl">
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-orange-700"><Landmark className="h-3.5 w-3.5" /> {t("banksPg.kicker")}</p>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-foreground">{t("banksPg.h1")}</h1>
            <p className="mt-4 text-muted-foreground"><Trans i18nKey="banksPg.lead" components={{ b: <span className="text-foreground font-medium" /> }} /></p>
            <Button variant="outline" className="mt-5 rounded-sm border-slate-300 bg-card hover:bg-secondary" onClick={() => navigate("/bank-compare")} data-testid="banks-compare-btn">
              <Scale className="h-4 w-4 mr-1.5 text-orange-700" /> {t("banksPg.compareBtn")}
            </Button>
            <div className="mt-7 flex flex-col gap-3">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("banksPg.search")} className="pl-9 bg-card border-border" data-testid="banks-search" />
              </div>
              <div className="flex flex-wrap items-center gap-2" data-testid="banks-type-filters">
                <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mr-1">{t("banksPg.type")}</span>
                {["All", "Digital", "Traditional"].map((ty) => (
                  <button key={ty} onClick={() => setTypeSel(ty)} className={`rounded-sm px-3 py-1 text-xs border transition-colors ${typeSel === ty ? "bg-orange-50 text-orange-700 border-orange-300" : "bg-card text-muted-foreground border-border hover:text-foreground"}`} data-testid={`banks-type-${ty.toLowerCase()}`}>{ty === "All" ? t("banksPg.all") : t(`cmp.type${ty}`)}</button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14 space-y-12">
        {groups.filter((g) => typeSel === "All" || typeSel === g.key).map((g) => {
          const items = banks.filter((b) => b.type === g.key && (!ql || b.name.toLowerCase().includes(ql)));
          if (!items.length) return null;
          return (
            <div key={g.key}>
              <div className="flex items-center gap-2 mb-6"><g.icon className="h-4 w-4 text-orange-700" /><h2 className="font-heading text-2xl text-primary">{g.label}</h2></div>
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {items.map((b, i) => (
                  <motion.div key={b.slug} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.04 }}
                    className="relative flex flex-col rounded-xl border border-border bg-card p-6 card-soft card-hover" data-testid={`bank-card-${b.slug}`}>
                    <label className="absolute top-4 right-4 z-10 flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none" data-testid={`bank-compare-toggle-${b.slug}`}>
                      <input type="checkbox" checked={compareSel.includes(b.slug)} onChange={() => toggleCompare(b.slug)} className="h-4 w-4 rounded border-slate-300 accent-orange-600 cursor-pointer" /> {t("banksPg.compare")}
                    </label>
                    <span className="grid place-items-center h-11 w-11 rounded-lg bg-secondary text-primary mb-4"><Landmark className="h-5 w-5" /></span>
                    <h3 className="font-heading text-xl text-primary">{b.name}</h3>
                    <span className={`mt-1.5 inline-flex w-fit items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 ${b.type === "Digital" ? "text-blue-700 bg-blue-100" : "text-slate-700 bg-slate-100"}`}>{b.type === "Digital" ? <Zap className="h-3 w-3" /> : <Landmark className="h-3 w-3" />} {b.type === "Digital" ? t("banksPg.fintech") : t("banksPg.bank")}</span>
                    <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"><Globe className="h-3.5 w-3.5 text-orange-600" /> {t(b.country_count === 1 ? "banksPg.jurOne" : "banksPg.jurMany", { count: b.country_count })}</p>
                    <div className="mt-4 flex items-baseline gap-1.5"><span className="font-heading text-3xl tracking-tight text-foreground">{formatUSD(b.fee)}</span><span className="text-xs text-muted-foreground">{t("banksPg.supportFee")}</span></div>
                    <div className="mt-6 flex gap-2">
                      <Button onClick={() => navigate(`/start?pkg=bank_${b.slug}`)} className="flex-1 rounded-sm bg-orange-500 text-white hover:bg-orange-600 h-9 text-sm" data-testid={`bank-order-${b.slug}`}>{t("banksPg.order")}</Button>
                      <Button variant="outline" onClick={() => navigate(`/bank/${b.slug}`)} className="flex-1 rounded-sm border-slate-300 bg-card hover:bg-secondary h-9 text-sm" data-testid={`bank-view-${b.slug}`}>{t("banksPg.details")}</Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          );
        })}

        <div className="rounded-xl border border-border bg-card p-6 card-soft flex flex-col sm:flex-row sm:items-center gap-4" data-testid="banks-payment-solutions">
          <span className="grid place-items-center h-11 w-11 rounded-lg bg-orange-50 text-orange-600 border border-orange-100 shrink-0"><CreditCard className="h-5 w-5" /></span>
          <div className="flex-1">
            <h2 className="font-heading text-xl text-primary">{t("banksPg.payTitle")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("banksPg.payBody")}</p>
          </div>
          <Button variant="outline" className="rounded-sm border-slate-300 bg-card hover:bg-secondary shrink-0" onClick={() => navigate("/payment-gateway/")} data-testid="banks-payment-solutions-btn">{t("banksPg.payBtn")} <ArrowRight className="h-4 w-4 ml-1.5 text-orange-600" /></Button>
        </div>
      </section>
      <Footer />

      {compareSel.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-2xl rounded-2xl border border-border bg-card card-soft shadow-lg px-4 py-3 flex items-center gap-3" data-testid="bank-compare-tray">
          <div className="flex flex-wrap gap-2 flex-1">
            {compareSel.map((slug) => (
              <span key={slug} className="flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs text-foreground" data-testid={`bank-tray-chip-${slug}`}>
                {nameOf(slug)}
                <button onClick={() => setCompareSel((p) => p.filter((s) => s !== slug))} className="text-muted-foreground hover:text-foreground"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
          <Button onClick={goCompare} disabled={compareSel.length < 2} className="rounded-sm bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 shrink-0" data-testid="bank-tray-go">{t("banksPg.compareN", { n: compareSel.length })}</Button>
        </motion.div>
      )}
    </div>
  );
}
